import React, { useState, useEffect, useContext } from 'react';
import { FaMotorcycle, FaCar, FaPhone, FaEdit, FaTrash, FaChevronDown, FaChevronUp, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import Alert from '../common/Alert';
import IconButton from '../common/IconButton';
import GuestVehicleForm from './GuestVehicleForm';
import useApi from '../../hooks/useApi';
import api from '../../api';
import Pagination from '../common/Pagination';
import CommunityIdentity from '../common/CommunityIdentity';

function GuestVehicleDashboard() {
  const { session } = useContext(AuthContext);
  const { roles, communityId } = session || { roles: ['GUEST'], communityId: null };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isAdmin = roles.includes('ADMIN');
  const isSecurity = roles.includes('SECURITY');
  const isUser = roles.includes('USER') || isAdmin || isSecurity || isSuperAdmin;
  const isPrivileged = isAdmin || isSecurity || isSuperAdmin;
  const [guestVehicles, setGuestVehicles] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState(isSuperAdmin ? '' : communityId || '');
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [history, setHistory] = useState({ content: [], totalPages: 1, totalElements: 0 });
  const [historyPage, setHistoryPage] = useState(0);
  const [historyBusy, setHistoryBusy] = useState(false);
  const emptyHistoryFilters = { search: '', division: '', houseNo: '', category: '', status: '', from: '', to: '' };
  const [historyFilters, setHistoryFilters] = useState(emptyHistoryFilters);
  const [appliedHistoryFilters, setAppliedHistoryFilters] = useState(emptyHistoryFilters);
  const [editFormData, setEditFormData] = useState({
    vehicleNo: '',
    type: '',
    guestName: '',
    visitorCategory: 'GUEST',
    contactNo: '',
    purposeOfVisit: '',
    visitingHouseNo: '',
    division: '',
    entryTime: '',
    communityId: isSuperAdmin ? '' : communityId || '',
  });
  const { get, put, delete: del } = useApi();

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCommunities();
    }
    fetchGuestVehicles();
  }, [selectedCommunityId]);

  useEffect(() => {
    if (activeTab === 'history' && (!isSuperAdmin || selectedCommunityId)) fetchVisitorHistory();
  }, [activeTab, selectedCommunityId, historyPage, appliedHistoryFilters]);

  const fetchCommunities = async () => {
    try {
      const response = await get('/api/communities/active');
      if (!Array.isArray(response)) {
        throw new Error('Invalid communities response format');
      }
      setCommunities(response);
    } catch (err) {
      console.error('GuestVehicleDashboard - Fetch communities error:', err);
      setError('Failed to load communities.');
      toast.error('Failed to load communities.');
    }
  };

  const fetchGuestVehicles = async () => {
    try {
      const params = (isSuperAdmin && selectedCommunityId) || (!isSuperAdmin && communityId) ? { communityId: selectedCommunityId || communityId } : {};
      const response = await get('/api/guest-vehicles', { params });
      setGuestVehicles([...(response || [])].sort((a, b) => new Date(b.entryTime) - new Date(a.entryTime)));
      setError('');
    } catch (err) {
      console.error('GuestVehicleDashboard - Fetch error:', err);
      setError('Failed to load visitors: ' + err.message);
      toast.error('Failed to load visitors.');
    }
  };

  const historyParams = (includePage = true) => {
    const filters = Object.fromEntries(Object.entries(appliedHistoryFilters).filter(([, value]) => value));
    return { ...filters, ...(selectedCommunityId || communityId ? { communityId: selectedCommunityId || communityId } : {}), ...(includePage ? { page: historyPage, size: 20 } : {}) };
  };

  const fetchVisitorHistory = async () => {
    try {
      setHistoryBusy(true);
      const response = await get('/api/guest-vehicles/history', { params: historyParams() });
      setHistory(response || { content: [], totalPages: 1, totalElements: 0 });
      setError('');
    } catch (err) {
      setError('Failed to load visitor history: ' + err.message);
    } finally {
      setHistoryBusy(false);
    }
  };

  const applyHistoryFilters = (event) => {
    event.preventDefault();
    setHistoryPage(0);
    setAppliedHistoryFilters({ ...historyFilters });
  };

  const clearHistoryFilters = () => {
    setHistoryFilters(emptyHistoryFilters);
    setAppliedHistoryFilters(emptyHistoryFilters);
    setHistoryPage(0);
  };

  const exportVisitorHistory = async (format) => {
    try {
      setHistoryBusy(true);
      const response = await api.get(`/api/guest-vehicles/history.${format}`, { params: historyParams(false), responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `visitor-history.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || `Unable to export visitor ${format.toUpperCase()} report.`);
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleEntry = async (id) => {
    try {
      await put(`/api/guest-vehicles/${id}/entry`);
      await fetchGuestVehicles();
      toast.success('Visitor marked as arrived.');
    } catch (err) {
      console.error('GuestVehicleDashboard - Mark entry error:', err);
      setError('Failed to mark entry: ' + err.message);
      toast.error('Failed to mark entry.');
    }
  };

  const handleDecision = async (id, decision) => {
    try {
      await put(`/api/guest-vehicles/${id}/${decision}`);
      await fetchGuestVehicles();
      toast.success(`Visitor ${decision === 'approve' ? 'approved and marked as arrived' : 'rejected'}.`);
    } catch (err) {
      toast.error(err.message || `Unable to ${decision} visitor.`);
    }
  };

  const handleExit = async (id) => {
    try {
      await put(`/api/guest-vehicles/${id}/exit`);
      await fetchGuestVehicles();
      toast.success('Exit marked.');
    } catch (err) {
      console.error('GuestVehicleDashboard - Mark exit error:', err);
      setError('Failed to mark exit: ' + err.message);
      toast.error('Failed to mark exit.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await del(`/api/guest-vehicles/${id}`);
      await fetchGuestVehicles();
      toast.success('Vehicle deleted.');
    } catch (err) {
      console.error('GuestVehicleDashboard - Delete error:', err);
      setError('Failed to delete vehicle: ' + err.message);
      toast.error('Failed to delete vehicle.');
    }
  };

  const handleEdit = (vehicle) => {
    setEditFormData({
      vehicleNo: vehicle.vehicleNo || '',
      type: vehicle.type || '',
      guestName: vehicle.guestName || vehicle.visitorName || '',
      visitorCategory: vehicle.visitorCategory || 'GUEST',
      contactNo: vehicle.contactNo || '',
      purposeOfVisit: vehicle.purposeOfVisit || vehicle.purpose || '',
      visitingHouseNo: vehicle.visitingHouseNo || '',
      division: vehicle.division || '',
      entryTime: vehicle.entryTime ? vehicle.entryTime.slice(0, 16) : '',
      communityId: vehicle.communityId || (isSuperAdmin ? '' : communityId || ''),
    });
    setEditingId(vehicle.id);
    if (isSuperAdmin) {
      setSelectedCommunityId(vehicle.communityId);
    }
    setExpandedVehicle(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if ((isAdmin || isSecurity) && !communityId) {
      setError('Community not assigned.');
      toast.error('Community not assigned.');
      return;
    }
    if (isSuperAdmin && !editFormData.communityId) {
      setError('Please select a community.');
      toast.error('Please select a community.');
      return;
    }
    try {
      await put(`/api/guest-vehicles/${editingId}`, editFormData);
      await fetchGuestVehicles();
      setEditingId(null);
      setEditFormData({
        vehicleNo: '',
        type: '',
        guestName: '',
        visitorCategory: 'GUEST',
        contactNo: '',
        purposeOfVisit: '',
        visitingHouseNo: '',
        division: '',
        entryTime: '',
        communityId: isSuperAdmin ? '' : communityId || '',
      });
      toast.success('Vehicle updated.');
    } catch (err) {
      console.error('GuestVehicleDashboard - Edit submit error:', err);
      setError('Failed to update vehicle: ' + err.message);
      toast.error('Failed to update vehicle.');
    }
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleCommunityChange = (e) => {
    const newCommunityId = e.target.value;
    setSelectedCommunityId(newCommunityId);
    setEditFormData({ ...editFormData, communityId: newCommunityId });
  };

  const toggleExpand = (id) => {
    setExpandedVehicle(expandedVehicle === id ? null : id);
    if (editingId) setEditingId(null);
  };

  if (!isUser) {
    return <Alert type="warning" message="Access denied. You must be logged in to view visitors." />;
  }

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4"><div><span className="vehicle-panel-kicker">Gate & security</span><h2 className="mb-0 fs-4 fw-bold">Visitors</h2></div>{activeTab==='active'&&<button type="button" className="btn btn-primary" onClick={() => setShowCreate(value => !value)}>{showCreate ? 'Close form' : 'Create visitor'}</button>}</div>
      {error && <Alert type="danger" message={error} />}

      <nav className="nav nav-pills gap-2 mb-3" aria-label="Visitor views">
        <button type="button" className={`nav-link ${activeTab==='active'?'active':''}`} onClick={()=>setActiveTab('active')}>Active visitors</button>
        <button type="button" className={`nav-link ${activeTab==='history'?'active':''}`} onClick={()=>setActiveTab('history')}>Visitor history</button>
      </nav>

      {isSuperAdmin && (
        <div className="mb-3">
          <label htmlFor="viewCommunityFilter" className="form-label">
            View Vehicles for Community
          </label>
          <select
            id="viewCommunityFilter"
            className="form-select"
            value={selectedCommunityId}
            onChange={handleCommunityChange}
            disabled={communities.length === 0}
          >
            <option value="">All Communities</option>
            {communities.map((community) => (
              <option key={community.id} value={community.id}>
                {community.displayName}
              </option>
            ))}
          </select>
        </div>
      )}

      {activeTab==='active' && editingId && (
        <div className="card shadow mb-4 animate__animated animate__slideInUp mx-auto" style={{ maxWidth: '800px' }}>
          <div className="card-body">
            <h5 className="card-title fs-5">Edit Visitor</h5>
            <form onSubmit={handleEditSubmit}>
              {isSuperAdmin && (
                <div className="mb-3">
                  <label htmlFor="communityFilter" className="form-label">
                    Select Community
                  </label>
                  <select
                    id="communityFilter"
                    className="form-select"
                    name="communityId"
                    value={editFormData.communityId}
                    onChange={handleEditChange}
                    disabled={communities.length === 0}
                    required
                  >
                    <option value="">Select a Community</option>
                    {communities.map((community) => (
                      <option key={community.id} value={community.id}>
                        {community.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="row g-3">
                <div className="col-12">
                  <label htmlFor="vehicleNo" className="form-label">Vehicle Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="vehicleNo"
                    value={editFormData.vehicleNo}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Vehicle Type</label>
                  <div className="d-flex flex-column flex-sm-row gap-3">
                    <div className="form-check flex-fill">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="edit-type-two-wheeler"
                        name="type"
                        value="two-wheeler"
                        checked={editFormData.type === 'two-wheeler'}
                        onChange={handleEditChange}
                        required
                      />
                      <label className="form-check-label d-flex align-items-center p-2 rounded bg-light" htmlFor="edit-type-two-wheeler">
                        <FaMotorcycle size={24} color="#007bff" className="me-2" />
                        Two-Wheeler
                      </label>
                    </div>
                    <div className="form-check flex-fill">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="edit-type-four-wheeler"
                        name="type"
                        value="four-wheeler"
                        checked={editFormData.type === 'four-wheeler'}
                        onChange={handleEditChange}
                        required
                      />
                      <label className="form-check-label d-flex align-items-center p-2 rounded bg-light" htmlFor="edit-type-four-wheeler">
                        <FaCar size={24} color="#28a745" className="me-2" />
                        Four-Wheeler
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <label htmlFor="guestName" className="form-label">Visitor Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="guestName"
                    value={editFormData.guestName}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="contactNo" className="form-label">Contact Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="contactNo"
                    value={editFormData.contactNo}
                    onChange={handleEditChange}
                    pattern="[0-9]{10}"
                    required
                    disabled={roles.includes('USER') && !isPrivileged}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="purposeOfVisit" className="form-label">Purpose of Visit</label>
                  <input
                    type="text"
                    className="form-control"
                    name="purposeOfVisit"
                    value={editFormData.purposeOfVisit}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="visitingHouseNo" className="form-label">Visiting House Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="visitingHouseNo"
                    value={editFormData.visitingHouseNo}
                    onChange={handleEditChange}
                    required
                    disabled={roles.includes('USER') && !isPrivileged}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="division" className="form-label">Division</label>
                  {roles.includes('USER') && !isPrivileged ? (
                    <input
                      type="text"
                      className="form-control"
                      name="division"
                      value={editFormData.division}
                      disabled
                    />
                  ) : (
                    <select
                      className="form-select"
                      name="division"
                      value={editFormData.division}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Select Division</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  )}
                </div>
                <div className="col-12">
                  <label htmlFor="entryTime" className="form-label">Entry Time</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="entryTime"
                    value={editFormData.entryTime}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-100 mt-3">Update Vehicle</button>
              <button
                type="button"
                className="btn btn-secondary w-100 mt-2"
                onClick={() => setEditingId(null)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab==='active'&&<div className="card shadow mx-auto" style={{ maxWidth: '1200px' }}>
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3">Active visitors</h5>
          {guestVehicles.length === 0 ? (
            <Alert type="info" message="No active visitors found." />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th style={{ width: '12%' }}>Vehicle Number</th>
                      <th style={{ width: '8%' }}>Type</th>
                      <th style={{ width: '12%' }}>Visitor</th>
                      <th style={{ width: '9%' }}>Category</th>
                      <th style={{ width: '12%' }}>Contact Number</th>
                      <th style={{ width: '15%' }}>Purpose</th>
                      <th style={{ width: '8%' }}>House No</th>
                      <th style={{ width: '8%' }}>Division</th>
                      <th style={{ width: '15%' }}>Entry Time</th>
                      <th style={{ width: '10%' }}>Status</th>
                      {isSuperAdmin && <th style={{ width: '10%' }}>Community</th>}
                      <th style={{ width: '15%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestVehicles.map((vehicle) => {
                      const canManage = vehicle.manageable === true;
                      return (
                        <tr key={vehicle.id}>
                          <td>{vehicle.vehicleNo}</td>
                          <td>
                            {vehicle.type === 'two-wheeler' ? (
                              <FaMotorcycle size={18} color="#007bff" title="Two-Wheeler" />
                            ) : (
                              <FaCar size={18} color="#28a745" title="Four-Wheeler" />
                            )}
                          </td>
                          <td>{vehicle.guestName || vehicle.visitorName}</td>
                          <td><span className="badge bg-primary-subtle text-primary-emphasis">{vehicle.visitorCategory || 'GUEST'}</span></td>
                          <td>
                            {vehicle.contactNo}{' '}
                            {vehicle.contactNo && (
                              <a href={`tel:${vehicle.contactNo}`} className="text-success ms-1">
                                <FaPhone size={14} />
                              </a>
                            )}
                          </td>
                          <td>{vehicle.purposeOfVisit || vehicle.purpose}</td>
                          <td>{vehicle.visitingHouseNo}</td>
                          <td>{vehicle.division}</td>
                          <td>{vehicle.entryTime ? new Date(vehicle.entryTime).toLocaleString() : '-'}</td>
                          <td>
                            <span
                              className={`badge ${
                                vehicle.status === 'PLANNED'
                                  ? 'bg-warning'
                                  : vehicle.status === 'ARRIVED'
                                  ? 'bg-success'
                                  : 'bg-danger'
                              }`}
                            >
                              {vehicle.status}
                            </span>
                          </td>
                          {isSuperAdmin && (
                            <td>{vehicle.community ? <CommunityIdentity community={vehicle.community} size="xs" compact /> : '-'}</td>
                          )}
                          <td>
                            {canManage && (
                              <>
                                <IconButton
                                  icon={<FaEdit />}
                                  tooltip="Edit"
                                  variant="warning"
                                  onClick={() => handleEdit(vehicle)}
                                  size="sm"
                                  className="me-1"
                                />
                                <IconButton
                                  icon={<FaTrash />}
                                  tooltip="Delete"
                                  variant="danger"
                                  onClick={() => handleDelete(vehicle.id)}
                                  size="sm"
                                  className="me-1"
                                />
                              </>
                            )}
                            {vehicle.approvable && (
                              <>
                                <IconButton icon={<FaCheck />} tooltip="Approve" variant="success" onClick={() => handleDecision(vehicle.id, 'approve')} size="sm" className="me-1" />
                                <IconButton icon={<FaTimes />} tooltip="Reject" variant="danger" onClick={() => handleDecision(vehicle.id, 'reject')} size="sm" className="me-1" />
                              </>
                            )}
                            {vehicle.status === 'PLANNED' && canManage && (
                              <IconButton
                                icon={<FaEdit />}
                                tooltip="Mark Entry"
                                variant="success"
                                onClick={() => handleEntry(vehicle.id)}
                                size="sm"
                                className="me-1"
                              />
                            )}
                            {vehicle.status === 'ARRIVED' && canManage && (
                              <IconButton
                                icon={<FaEdit />}
                                tooltip="Mark Exit"
                                variant="danger"
                                onClick={() => handleExit(vehicle.id)}
                                size="sm"
                              />
                            )}
                            {!canManage && !vehicle.approvable && <span className="read-only-pill">View only</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Mobile Cards */}
              <div className="d-md-none">
                {guestVehicles.map((vehicle) => {
                  const canManage = vehicle.manageable === true;
                  const isExpanded = expandedVehicle === vehicle.id;
                  return (
                    <div
                      key={vehicle.id}
                      className="card shadow-sm mb-3 animate__animated animate__fadeInUp"
                    >
                      <div
                        className="card-header d-flex justify-content-between align-items-center bg-light"
                        onClick={() => toggleExpand(vehicle.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="mb-0">
                          {vehicle.vehicleNo} - {vehicle.guestName || vehicle.visitorName}
                        </h6>
                        <span
                          className={`badge ${
                            vehicle.status === 'PLANNED'
                              ? 'bg-warning'
                              : vehicle.status === 'ARRIVED'
                              ? 'bg-success'
                              : 'bg-danger'
                          }`}
                        >
                          {vehicle.status}
                        </span>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong>Type:</strong>{' '}
                            {vehicle.type === 'two-wheeler' ? 'Two-Wheeler' : 'Four-Wheeler'}
                          </div>
                          {vehicle.type === 'two-wheeler' ? (
                            <FaMotorcycle size={18} color="#007bff" />
                          ) : (
                            <FaCar size={18} color="#28a745" />
                          )}
                        </div>
                        {isExpanded && (
                          <div className="mt-2">
                            <p className="mb-1">
                              <strong>Category:</strong> {vehicle.visitorCategory || 'GUEST'}
                            </p>
                            <p className="mb-1">
                              <strong>Contact:</strong>{' '}
                              {vehicle.contactNo ? (
                                <>
                                  {vehicle.contactNo}{' '}
                                  <a href={`tel:${vehicle.contactNo}`} className="text-success">
                                    <FaPhone size={14} />
                                  </a>
                                </>
                              ) : (
                                '-'
                              )}
                            </p>
                            <p className="mb-1">
                              <strong>Purpose:</strong> {vehicle.purposeOfVisit || vehicle.purpose || '-'}
                            </p>
                            <p className="mb-1">
                              <strong>House No:</strong> {vehicle.visitingHouseNo || '-'}
                            </p>
                            <p className="mb-1">
                              <strong>Division:</strong> {vehicle.division || '-'}
                            </p>
                            <p className="mb-1">
                              <strong>Entry Time:</strong>{' '}
                              {vehicle.entryTime ? new Date(vehicle.entryTime).toLocaleString() : '-'}
                            </p>
                            {isSuperAdmin && (
                              <p className="mb-1">
                                <strong>Community:</strong>{' '}
                                {vehicle.community ? <CommunityIdentity community={vehicle.community} size="xs" compact /> : '-'}
                              </p>
                            )}
                          </div>
                        )}
                        {(canManage || vehicle.approvable) && (
                          <div className="d-flex flex-wrap gap-2 mt-3">
                            {canManage && <><button
                              className="btn btn-warning btn-sm flex-grow-1 d-flex align-items-center justify-content-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(vehicle);
                              }}
                            >
                              <FaEdit className="me-1" /> Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm flex-grow-1 d-flex align-items-center justify-content-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(vehicle.id);
                              }}
                            >
                              <FaTrash className="me-1" /> Delete
                            </button></>}
                            {vehicle.approvable && (
                              <>
                                <button className="btn btn-success btn-sm" onClick={(e) => { e.stopPropagation(); handleDecision(vehicle.id, 'approve'); }}><FaCheck className="me-1" />Approve</button>
                                <button className="btn btn-outline-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleDecision(vehicle.id, 'reject'); }}><FaTimes className="me-1" />Reject</button>
                              </>
                            )}
                            {vehicle.status === 'PLANNED' && canManage && (
                              <button
                                className="btn btn-success btn-sm flex-grow-1 d-flex align-items-center justify-content-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEntry(vehicle.id);
                                }}
                              >
                                Mark Entry
                              </button>
                            )}
                            {vehicle.status === 'ARRIVED' && canManage && (
                              <button
                                className="btn btn-danger btn-sm flex-grow-1 d-flex align-items-center justify-content-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExit(vehicle.id);
                                }}
                              >
                                Mark Exit
                              </button>
                            )}
                          </div>
                        )}
                        {!canManage && !vehicle.approvable && (
                          <div className="read-only-note">Shared community view · only the host or gate team can update this visit.</div>
                        )}
                      </div>
                      <div
                        className="card-footer text-center bg-light"
                        onClick={() => toggleExpand(vehicle.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {isExpanded ? (
                          <FaChevronUp size={16} className="text-muted" />
                        ) : (
                          <FaChevronDown size={16} className="text-muted" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>}

      {activeTab==='history'&&(!isSuperAdmin||selectedCommunityId)&&<section className="visitor-history-panel">
        <form className="visitor-history-filters" onSubmit={applyHistoryFilters}>
          <div className="visitor-history-heading"><div><h3>Visitor history</h3><p>Search completed, departed, rejected and planned visits retained by your community.</p></div><strong>{history.totalElements||0} records</strong></div>
          <label className="visitor-filter-search">Visitor, vehicle or contact<input type="search" className="form-control" value={historyFilters.search} onChange={e=>setHistoryFilters({...historyFilters,search:e.target.value})} placeholder="Search visitor, vehicle or phone"/></label>
          {isPrivileged&&<label>Division<input className="form-control" value={historyFilters.division} onChange={e=>setHistoryFilters({...historyFilters,division:e.target.value})} placeholder="All divisions"/></label>}
          {isPrivileged&&<label>House number<input className="form-control" value={historyFilters.houseNo} onChange={e=>setHistoryFilters({...historyFilters,houseNo:e.target.value})} placeholder="All houses"/></label>}
          <label>Category<select className="form-select" value={historyFilters.category} onChange={e=>setHistoryFilters({...historyFilters,category:e.target.value})}><option value="">All categories</option>{['GUEST','DELIVERY','SERVICE','CAB','VENDOR','OTHER'].map(value=><option key={value}>{value}</option>)}</select></label>
          <label>Status<select className="form-select" value={historyFilters.status} onChange={e=>setHistoryFilters({...historyFilters,status:e.target.value})}><option value="">All statuses</option>{['PLANNED','ARRIVED','DEPARTED','REJECTED','CANCELLED'].map(value=><option key={value}>{value}</option>)}</select></label>
          <label>Entry from<input type="date" className="form-control" value={historyFilters.from} onChange={e=>setHistoryFilters({...historyFilters,from:e.target.value})}/></label>
          <label>Entry to<input type="date" className="form-control" value={historyFilters.to} onChange={e=>setHistoryFilters({...historyFilters,to:e.target.value})}/></label>
          <div className="visitor-history-actions"><button className="btn btn-primary" disabled={historyBusy}>Apply filters</button><button type="button" className="btn btn-outline-secondary" onClick={clearHistoryFilters}>Clear</button>{isPrivileged&&<><button type="button" className="btn btn-outline-primary" disabled={historyBusy} onClick={()=>exportVisitorHistory('csv')}>Export CSV</button><button type="button" className="btn btn-outline-danger" disabled={historyBusy} onClick={()=>exportVisitorHistory('pdf')}>Export PDF</button></>}</div>
        </form>
        <div className="table-responsive">
          <table className="table align-middle visitor-history-table"><thead><tr><th>Entry</th><th>Visitor</th><th>Category</th><th>Vehicle / type</th><th>Contact</th>{isPrivileged&&<th>Division / house</th>}<th>Purpose</th><th>Exit</th><th>Status</th></tr></thead>
          <tbody>{history.content?.map(visitor=><tr key={visitor.id}><td>{visitor.entryTime?new Date(visitor.entryTime).toLocaleString('en-IN'):'-'}</td><td><strong>{visitor.guestName}</strong></td><td><span className="badge bg-primary-subtle text-primary-emphasis">{visitor.visitorCategory}</span></td><td>{visitor.vehicleNo}<small className="d-block text-muted">{visitor.type}</small></td><td>{visitor.contactNo}</td>{isPrivileged&&<td>{visitor.division} / {visitor.visitingHouseNo}</td>}<td>{visitor.purposeOfVisit}</td><td>{visitor.exitTime?new Date(visitor.exitTime).toLocaleString('en-IN'):'-'}</td><td><span className="badge bg-secondary-subtle text-secondary-emphasis">{visitor.status}</span></td></tr>)}</tbody></table>
          {!historyBusy&&!history.content?.length&&<div className="engagement-empty">No visitor records match these filters.</div>}
        </div>
        {history.totalPages>1&&<Pagination currentPage={historyPage} totalPages={history.totalPages} onPageChange={setHistoryPage}/>}
      </section>}
      {activeTab==='history'&&isSuperAdmin&&!selectedCommunityId&&<Alert type="info" message="Select a community to view and export visitor history."/>}

      {activeTab==='active'&&showCreate && <div className="card shadow mx-auto" style={{ maxWidth: '1200px' }}>
        {['USER', 'SECURITY', 'ADMIN', 'SUPER_ADMIN'].some((role) => roles.includes(role)) && (
          <div className="mb-4">
            <GuestVehicleForm onSuccess={fetchGuestVehicles} />
          </div>
        )}
      </div>}
    </div>
  );
}

export default GuestVehicleDashboard;
